import React, { useEffect, useState } from 'react';
import { Popover } from 'antd';

import { FormattedMessage } from '../../util/reactIntl';
import { networkPagesFor } from './networkPages';

import css from './PreferredNetworkNavItem.module.css';

/**
 * The Preferred Network item of the left navigation, and the flyout that holds its pages.
 *
 * The flyout is a popover on this item alone: hovering over the item or clicking it opens it
 * beside the navigation, it stays open while the pointer is on it, and a click elsewhere or Esc
 * closes it.
 *
 * It renders in the document body, not inside the navigation. The navigation is a fixed column that
 * clips whatever overflows its box, so a flyout inside it has no room beside the item: it flips to
 * the item's left and is cut off at the edge of the screen. The flyout's root carries
 * `data-network-flyout`, so the navigation can treat a press in it as a press on itself and keep
 * its own open submenus (Account Settings) as they are.
 *
 * @param {Object} props
 * @param {'customer'|'agency'|'candidate'|null} props.userType
 * @param {function(string): void} props.onNavigate called with the route name of the chosen page
 * @param {'label'|'icon'} [props.variant] what the trigger shows: the item's words, or its icon
 * @param {React.ReactNode} [props.icon]
 * @param {string} [props.className] kept on the trigger: a collapsed menu marks an item's icon with a
 *   class and hides the words only when they follow the marked icon
 */
const PreferredNetworkNavItem = props => {
  const { userType, onNavigate, variant = 'label', icon = null, className } = props;
  const [open, setOpen] = useState(false);
  const pages = networkPagesFor(userType);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const closeOnEscape = event => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [open]);

  if (pages.length === 0) return null;

  const content = (
    // The menu around this item acts on clicks inside it; a choice in the flyout is not one of them.
    <nav className={css.flyout} data-network-flyout="" onClick={e => e.stopPropagation()} aria-label="Preferred Network">
      <ul className={css.links}>
        {pages.map(page => (
          <li key={page.key}>
            <button
              type="button"
              className={css.link}
              onClick={() => {
                setOpen(false);
                onNavigate(page.routeName);
              }}
            >
              <FormattedMessage id={page.labelId} />
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );

  return (
    <Popover
      content={content}
      trigger={['hover', 'click']}
      placement="rightTop"
      open={open}
      onOpenChange={setOpen}
      arrow={false}
    >
      <span
        className={className ? `${css.trigger} ${className}` : css.trigger}
        data-testid="preferred-network-trigger">
        {variant === 'icon' ? icon : <FormattedMessage id="PreferredNetwork.nav.title" />}
      </span>
    </Popover>
  );
};

export default PreferredNetworkNavItem;
